import { useState } from "react";
import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, ArrowLeft, MessageSquare, Navigation2, Bell, Zap } from "lucide-react";
import { FinanceApplicationForm } from "@/components/FinanceApplicationForm";
import holabotImg from "@/assets/robots/holabot.png";

const HolaBot = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);

  return (
    <>
      <SEO 
        title="PUDU HolaBot - Pager & Notification Delivery Robot | Neil AI"
        description="Compact delivery robot perfect for paging and notification delivery in restaurants and hospitality. Features smart navigation, multiple notification modes, and seamless integration."
        keywords="HolaBot, paging robot, notification robot, restaurant robot, hospitality robot, compact delivery robot, PUDU robotics"
      />
      
      <div className="min-h-screen bg-gradient-to-b from-background via-background to-primary/5">
        <Navigation />
        
        {/* Hero Section */}
        <section className="pt-44 pb-12 px-4 md:pt-48">
          <div className="container mx-auto">
            <Link to="/robots" className="inline-flex items-center text-primary hover:underline mb-6">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to All Robots
            </Link>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <Badge className="bg-primary/10 text-primary">Pager & Notification Delivery Robot</Badge>
                <h1 className="text-2xl md:text-4xl lg:text-5xl font-orbitron font-bold break-words">
                  PUDU HolaBot
                </h1>
                <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed">
                  Compact delivery robot perfect for paging and notification delivery in restaurants and hospitality environments.
                </p>
                
                <div className="grid grid-cols-2 gap-4 py-6">
                  <Card className="p-4 glass-effect border-primary/20">
                    <p className="text-2xl font-bold text-primary">Compact</p>
                    <p className="text-sm text-muted-foreground">Space-Efficient Design</p>
                  </Card>
                  <Card className="p-4 glass-effect border-primary/20">
                    <p className="text-2xl font-bold text-primary">Smart</p>
                    <p className="text-sm text-muted-foreground">Intelligent Navigation</p>
                  </Card>
                  <Card className="p-4 glass-effect border-primary/20">
                    <p className="text-2xl font-bold text-primary">Multi-Mode</p>
                    <p className="text-sm text-muted-foreground">Versatile Notifications</p>
                  </Card>
                  <Card className="p-4 glass-effect border-primary/20">
                    <p className="text-2xl font-bold text-primary">Integration</p>
                    <p className="text-sm text-muted-foreground">Seamless Paging System</p>
                  </Card>
                </div>

                <div className="flex flex-col sm:flex-row flex-wrap gap-4">
                  <Button size="lg" asChild className="glow-gold">
                    <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                      Book a Demo <ArrowRight className="ml-2" />
                    </a>
                  </Button>
                  <Button size="lg" variant="outline" onClick={() => setFinanceOpen(true)}>
                    Apply for Financing
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <Link to="/robot-recommender">Find Your Perfect Robot</Link>
                  </Button>
                </div>
              </div>

              <div className="relative">
                <div className="aspect-square bg-gradient-to-br from-primary/20 to-background rounded-2xl p-8 border border-primary/20">
                  <img 
                    src={holabotImg} 
                    alt="PUDU HolaBot" 
                    className="w-full h-full object-contain drop-shadow-2xl"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Tabs Section */}
        <section className="py-12 px-4">
          <div className="container mx-auto">
            <Tabs defaultValue="overview" className="w-full">
              <TabsList className="grid w-full grid-cols-3 max-w-2xl mx-auto mb-8">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="specs">Specifications</TabsTrigger>
                <TabsTrigger value="applications">Applications</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-8">
                <Card className="p-8 glass-effect border-primary/20">
                  <h2 className="text-3xl font-orbitron font-bold mb-6">Compact & Efficient Notification Delivery</h2>
                  <p className="text-lg text-muted-foreground mb-6">
                    PUDU HolaBot is designed specifically for paging and notification delivery in busy restaurant and hospitality environments. Its compact form factor allows it to navigate tight spaces while delivering timely notifications to guests and staff.
                  </p>
                  
                  <div className="grid md:grid-cols-2 gap-6 mt-8">
                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <Bell className="w-8 h-8 text-primary flex-shrink-0" />
                        <div>
                          <h3 className="font-bold text-lg mb-2">Paging System Integration</h3>
                          <p className="text-muted-foreground">Seamlessly integrates with existing paging systems to deliver notifications efficiently to customers.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4">
                        <Zap className="w-8 h-8 text-primary flex-shrink-0" />
                        <div>
                          <h3 className="font-bold text-lg mb-2">Compact Design</h3>
                          <p className="text-muted-foreground">Space-efficient design perfect for crowded restaurants and hospitality venues with limited floor space.</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="flex items-start gap-4">
                        <Navigation2 className="w-8 h-8 text-primary flex-shrink-0" />
                        <div>
                          <h3 className="font-bold text-lg mb-2">Smart Navigation</h3>
                          <p className="text-muted-foreground">Advanced navigation system ensures smooth movement through busy environments and around obstacles.</p>
                        </div>
                      </div>
                      
                      <div className="flex items-start gap-4">
                        <MessageSquare className="w-8 h-8 text-primary flex-shrink-0" />
                        <div>
                          <h3 className="font-bold text-lg mb-2">Multiple Notification Modes</h3>
                          <p className="text-muted-foreground">Supports various notification methods including audio, visual displays, and interactive touchscreen alerts.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 glass-effect border-primary/20 bg-gradient-to-br from-primary/5 to-background">
                  <h2 className="text-2xl font-orbitron font-bold mb-4">Key Benefits</h2>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Improves guest experience with timely, automated notifications</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Reduces staff workload by automating paging and notification tasks</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Compact size allows operation in tight spaces and crowded areas</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Easy integration with existing restaurant management systems</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-primary text-xl">✓</span>
                      <span>Professional appearance enhances brand image and customer perception</span>
                    </li>
                  </ul>
                </Card>
              </TabsContent>

              <TabsContent value="specs" className="space-y-6">
                <Card className="p-8 glass-effect border-primary/20">
                  <h2 className="text-3xl font-orbitron font-bold mb-6">Technical Specifications</h2>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Physical Dimensions</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Compact form factor for tight spaces</p>
                          <p>• Optimized height for customer interaction</p>
                          <p>• Lightweight and maneuverable design</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Navigation & Sensors</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Advanced obstacle detection and avoidance</p>
                          <p>• Precise positioning for accurate notifications</p>
                          <p>• Multi-sensor fusion for safe operation</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Power & Performance</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Long-lasting battery for full-day operation</p>
                          <p>• Auto-charging capability</p>
                          <p>• Energy-efficient operation</p>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Notification Capabilities</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Audio notification system</p>
                          <p>• Visual display for messages</p>
                          <p>• Interactive touchscreen interface</p>
                          <p>• Customizable notification sounds</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Integration</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• Paging system compatibility</p>
                          <p>• Restaurant POS integration</p>
                          <p>• Cloud-based management platform</p>
                          <p>• Real-time status monitoring</p>
                        </div>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-4 text-primary">Safety Features</h3>
                        <div className="space-y-2 text-muted-foreground">
                          <p>• 360° obstacle detection</p>
                          <p>• Emergency stop system</p>
                          <p>• Collision prevention technology</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="applications" className="space-y-6">
                <Card className="p-8 glass-effect border-primary/20">
                  <h2 className="text-3xl font-orbitron font-bold mb-6">Industry Applications</h2>
                  
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold mb-3 text-primary">Restaurants</h3>
                        <p className="text-muted-foreground mb-3">
                          Perfect for fast-casual and quick-service restaurants needing efficient guest notification systems.
                        </p>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Table-ready notifications</li>
                          <li>• Order pickup alerts</li>
                          <li>• Wait list management</li>
                          <li>• Seating notifications</li>
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-3 text-primary">Food Courts</h3>
                        <p className="text-muted-foreground mb-3">
                          Ideal for busy food courts with multiple vendors and high customer traffic.
                        </p>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Order ready notifications</li>
                          <li>• Multi-vendor coordination</li>
                          <li>• Crowd management</li>
                          <li>• Customer engagement</li>
                        </ul>
                      </div>
                    </div>

                    <div className="space-y-6">
                      <div>
                        <h3 className="text-xl font-bold mb-3 text-primary">Hotels & Hospitality</h3>
                        <p className="text-muted-foreground mb-3">
                          Enhance guest experiences in hotels with automated notification delivery.
                        </p>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Guest paging and notifications</li>
                          <li>• Event and activity reminders</li>
                          <li>• Concierge notifications</li>
                          <li>• Service request updates</li>
                        </ul>
                      </div>

                      <div>
                        <h3 className="text-xl font-bold mb-3 text-primary">Cafes & Coffee Shops</h3>
                        <p className="text-muted-foreground mb-3">
                          Streamline operations in busy cafes with compact notification delivery.
                        </p>
                        <ul className="space-y-2 text-muted-foreground">
                          <li>• Order completion alerts</li>
                          <li>• Name-based notifications</li>
                          <li>• Queue management</li>
                          <li>• Customer engagement</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </Card>

                <Card className="p-8 glass-effect border-primary/20 bg-gradient-to-br from-primary/5 to-background">
                  <h2 className="text-2xl font-orbitron font-bold mb-4">Why Choose HolaBot?</h2>
                  <p className="text-muted-foreground mb-6">
                    HolaBot combines compact design with intelligent notification delivery to create the perfect solution for busy hospitality environments. Its small footprint and smart features make it an invaluable asset for any customer-facing operation.
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <Button size="lg" asChild className="glow-gold">
                      <a href="https://api.leadconnectorhq.com/widget/bookings/neilaipudurobotics" target="_blank" rel="noopener noreferrer">
                        Schedule Your Demo <ArrowRight className="ml-2" />
                      </a>
                    </Button>
                    <Button size="lg" variant="outline" asChild>
                      <Link to="/restaurants">View Restaurant Solutions</Link>
                    </Button>
                  </div>
                </Card>
              </TabsContent>
            </Tabs>
          </div>
        </section>

        <FinanceApplicationForm 
          open={financeOpen} 
          onOpenChange={setFinanceOpen}
          robotName="HolaBot"
        />

        <Footer />
      </div>
    </>
  );
};

export default HolaBot;
